'use client';

import { useEffect } from 'react';

/**
 * Ad-block MEASUREMENT beacon. Purely diagnostic — it serves no ads and
 * circumvents no blocker (AdSense-ToS-safe). It detects whether the visitor is
 * running an ad blocker and reports the boolean to two sinks:
 *
 *   1. D1 (primary) — POST to /api/session-signal, which enriches the row with
 *      country / device / browser / bot-ness server-side. Queryable straight
 *      away via `wrangler d1 execute --remote`.
 *   2. GA4 (secondary) — `window.zaraz.track`, Zaraz being proxied under
 *      inventivehq.com so it survives the very blocker being measured. Its
 *      params must be registered as GA4 custom dimensions to be readable at
 *      all, and GA4 never backfills — which is precisely why D1 is primary.
 *
 * Divergence between the two sinks is itself a signal that one is filtered.
 *
 * THIRD COPY — keep in sync with apps/blog/components/AdblockBeacon.tsx and
 * apps/tools/components/AdblockBeacon.tsx. The three worker apps don't share a
 * component package, so a fix applied to one silently misses the others (same
 * trap as the duplicated analytics.ts / DynamicToolLoader.tsx). If you change
 * the detection logic here, change it in all three or the surfaces stop being
 * comparable.
 *
 * CSP NOTE (unique to this app): the main app is the only one of the three that
 * sends a Content-Security-Policy. The network bait below loads
 * static.doubleclick.net, which is NOT covered by the googleads.g.doubleclick.net
 * entry (no wildcard). A CSP refusal fires the script's onerror handler, which
 * this code reads as "ad blocker present" — so a missing allow-list entry
 * reports 100% blocked for every visitor. static.doubleclick.net is allow-listed
 * in BOTH next.config.mjs and middleware.ts; it must stay in both.
 *
 * Two independent signals, OR-combined:
 *   - network-filter bait: the canonical `ad_status.js` URL blockers block
 *     (script `onerror`) — doesn't touch the page's real AdSense units.
 *   - cosmetic-filter bait: an ad-classed element blockers hide (offsetHeight 0).
 *
 * Fires once per session (sessionStorage guard) so the GA4 numbers read as a
 * per-visitor rate: blocked events / total events.
 *
 * Read it in GA4: event `adblock_status`, param `blocked` (true|false),
 * segmentable by `surface` and `path`. Those params must be registered as
 * event-scoped custom dimensions in GA4 Admin → Custom definitions, or the
 * data is collected but unqueryable (GA4 does not backfill).
 */
export default function AdblockBeacon({ surface }: { surface: string }) {
  useEffect(() => {
    // Per-session, not per-pageview — we want a per-visitor rate.
    try {
      if (sessionStorage.getItem('ab_checked')) return;
      sessionStorage.setItem('ab_checked', '1');
    } catch {
      // sessionStorage blocked (rare private-mode cases) — proceed anyway.
    }

    let reported = false;
    const report = (blocked: boolean, method: string) => {
      if (reported) return;
      reported = true;
      const payload = { blocked, method, surface, path: location.pathname };

      // Sink 1 (primary): D1 via a same-origin API route. Queryable
      // immediately and sliceable by the country/device/browser the server
      // attaches — neither of which GA4 can give us. `keepalive` so the write
      // survives the user navigating away mid-flight. The endpoint path is
      // deliberately inert; see the naming warning in
      // app/api/session-signal/route.ts before renaming anything.
      try {
        fetch('/api/session-signal', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
          keepalive: true,
        }).catch(() => {
          // Fire-and-forget; a failed write must never surface to the user.
        });
      } catch {
        // Best-effort; never throw into the page.
      }

      // Sink 2 (secondary): GA4 via Zaraz's first-party pipeline. Kept as a
      // cross-check — divergence between the two sinks is itself a signal that
      // one of them is being filtered.
      try {
        const z = (
          window as unknown as {
            zaraz?: { track?: (n: string, p: Record<string, unknown>) => void };
          }
        ).zaraz;
        z?.track?.('adblock_status', payload);
      } catch {
        // Best-effort; never throw into the page.
      }
    };

    // 1) Cosmetic bait — ad-ish classes that filter lists hide.
    const bait = document.createElement('div');
    bait.className = 'adsbox ad-banner ad-placement pub_300x250 text-ad';
    bait.style.cssText =
      'position:absolute;left:-9999px;top:-9999px;width:1px;height:1px;pointer-events:none;';
    bait.innerHTML = '&nbsp;';
    document.body.appendChild(bait);

    const cosmeticBlocked = () =>
      bait.offsetParent === null ||
      bait.offsetHeight === 0 ||
      bait.clientHeight === 0 ||
      getComputedStyle(bait).display === 'none';

    // 2) Network bait — canonical adblock-detection URL, independent of our ads.
    let netBlocked: boolean | null = null;
    const s = document.createElement('script');
    s.src = 'https://static.doubleclick.net/instream/ad_status.js';
    s.async = true;
    const settle = () => {
      const cos = cosmeticBlocked();
      report(netBlocked === true || cos, `net:${netBlocked} cos:${cos}`);
      bait.remove();
      s.remove();
    };
    s.onload = () => {
      netBlocked = false;
      settle();
    };
    s.onerror = () => {
      netBlocked = true;
      settle();
    };
    document.body.appendChild(s);

    // Fallback if neither handler fires (some blockers stub the node silently).
    const fallback = window.setTimeout(() => {
      if (!reported) {
        report(cosmeticBlocked(), 'timeout');
        bait.remove();
      }
    }, 3000);

    return () => window.clearTimeout(fallback);
  }, [surface]);

  return null;
}
